/**
 * Fusão de uma sugestão de correção de cadastro com o ativo que ela corrige.
 *
 * O `dados` da sugestão é um PATCH — só os campos a mudar (`{classe, subtipo}`). O modal de
 * cadastro (`consolidator-register-asset`) grava o formulário INTEIRO, então abrir com o patch
 * sozinho apagaria emissor, taxa, vencimento e o resto. Aqui o patch entra POR CIMA do ativo
 * atual, e o que a IA não citou permanece como está.
 *
 * Só os campos que o writer do backend aceita (`ATIVO_CLIENTE_CAMPOS_EDITAVEIS`) atravessam:
 * chave fora dessa lista é ignorada pelo aceite, e deixá-la entrar no formulário faria o
 * assessor ver uma proposta que nunca seria gravada.
 *
 * ★ O MESMO conceito tem NOME DIFERENTE nas duas camadas, e é o que fazia o select da tela
 * ignorar a sugestão: o backend chama `classe`/`subtipo`, o formulário chama `CI`/`categoria`.
 * O `asset-edit-adapter` resolve `CI` por `coalesceString(item.CI, ..., item.classe)` — e a linha
 * da carteira JÁ traz `CI` com o valor ANTIGO, que vence o `classe` da sugestão no primeiro
 * não-vazio. Por isso o patch escreve os DOIS nomes na ida, e a volta (`patchDoFormulario`) lê
 * pelo nome do FORMULÁRIO: depois que o assessor edita, quem mudou foi `CI`, não `classe`.
 *
 * ★ A limpeza da subclasse vale para os DOIS caminhos de aceite, e é por isso que ela tem uma
 * função própria (`dadosDoAceite`) além do preenchimento do formulário. O `Aprovar` direto não
 * passa por formulário nenhum: sem `dados` explícito, o backend aplica o patch da própria
 * sugestão (`{classe}`) e a subclasse antiga fica na linha, agora órfã. `editar_campos` grava
 * string vazia (só campos de posição são filtrados por `DATA_TO_NO_UPDATE`), então limpar pelo
 * patch funciona.
 *
 * ★ Subclasse é SUBORDINADA à classe. Sugestão que troca a classe sem propor subtipo deixaria a
 * subclasse antiga na tela, agora órfã de uma classe que não existe mais naquele ativo — e o
 * assessor salvaria a combinação inválida sem perceber. Por isso ela é LIMPA junto, e o próprio
 * formulário reofertar as opções da classe nova.
 */
export const CAMPOS_ACEITOS_NO_ATIVO = [
  'classe',
  'tipo',
  'subtipo',
  'referencia',
  'isin',
  'data_vencimento',
  'emissor',
  'taxa',
]

const NOME_NO_FORMULARIO = {
  classe: ['CI'],
  subtipo: ['categoria', 'subclasse'],
}

const SUBORDINADOS_A_CLASSE = ['subtipo', 'categoria', 'subclasse']

export const patchDaSugestao = (suggestion) => {
  const dados = (suggestion && suggestion.dados) || {}
  return CAMPOS_ACEITOS_NO_ATIVO.reduce((patch, campo) => {
    if (dados[campo] !== undefined && dados[campo] !== null) patch[campo] = dados[campo]
    return patch
  }, {})
}

export const patchNaLinguagemDoFormulario = (suggestion) => {
  const patch = patchDaSugestao(suggestion)
  const saida = Object.keys(patch).reduce((acc, campo) => {
    acc[campo] = patch[campo]
    ;(NOME_NO_FORMULARIO[campo] || []).forEach((alias) => {
      acc[alias] = patch[campo]
    })
    return acc
  }, {})

  if (patch.classe !== undefined && patch.subtipo === undefined) {
    SUBORDINADOS_A_CLASSE.forEach((campo) => {
      saida[campo] = ''
    })
  }

  return saida
}

export const ativoComSugestao = ({ asset, suggestion } = {}) => {
  if (!asset || !asset.id) return null
  return { ...asset, ...patchNaLinguagemDoFormulario(suggestion) }
}

export const camposSugeridos = (suggestion) => Object.keys(patchDaSugestao(suggestion))

export const dadosDoAceite = (suggestion) => {
  const patch = patchDaSugestao(suggestion)
  if (!Object.keys(patch).length) return null
  if (patch.classe !== undefined && patch.subtipo === undefined) patch.subtipo = ''
  return patch
}

/**
 * O formulário guarda o vencimento no formato de EXIBIÇÃO (`18/12/2025`); a coluna do banco é
 * `date`. Mandar o valor da tela crua no aceite derrubava a gravação com 500 — e como o ativo já
 * tinha sido salvo pelo caminho normal, o assessor via "editado com sucesso" e a sugestão
 * continuava Pendente. Só converte o que ESTÁ em dd/mm/aaaa: valor já ISO, vazio ou nulo passa
 * intacto, para não reescrever formato que o backend já entende.
 */
const DATA_BRASILEIRA = /^(\d{2})\/(\d{2})\/(\d{4})$/

export const dataParaOBackend = (valor) => {
  if (typeof valor !== 'string') return valor
  const brasileira = DATA_BRASILEIRA.exec(valor.trim())
  if (!brasileira) return valor
  const [, dia, mes, ano] = brasileira
  return `${ano}-${mes}-${dia}`
}

const CAMPOS_DE_DATA = new Set(['data_vencimento'])

export const patchDoFormulario = (formItem) => {
  const form = formItem || {}
  return CAMPOS_ACEITOS_NO_ATIVO.reduce((patch, campo) => {
    const nomes = [...(NOME_NO_FORMULARIO[campo] || []), campo]
    const usado = nomes.find(
      (nome) => form[nome] !== undefined && form[nome] !== null
    )
    if (usado === undefined) return patch
    patch[campo] = CAMPOS_DE_DATA.has(campo)
      ? dataParaOBackend(form[usado])
      : form[usado]
    return patch
  }, {})
}

export const resolveAtivoDaSugestao = ({ suggestion, contexto } = {}) => {
  const ctx = contexto || {}
  const alvo = suggestion && suggestion.id_entidade_alvo
  const lista = Array.isArray(ctx.ativos) ? ctx.ativos : []
  if (alvo !== null && alvo !== undefined && alvo !== '') {
    const achado = lista.find((ativo) => ativo && String(ativo.id) === String(alvo))
    if (achado) return achado
    if (ctx.asset && String(ctx.asset.id) === String(alvo)) return ctx.asset
    return null
  }
  return ctx.asset || null
}
